import React, { useState, useEffect } from "react";
import ThemedReaderFrame from "../components/ThemedReaderFrame";
import "../styles/VicharanDarshan.css";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const gujaratiCollator = new Intl.Collator("gu-IN");

function getDateKey(year, month, day) {
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function getYoutubeId(url) {
  if (!url || url === "NULL") return null;
  try {
    // youtube.com/watch?v=ID
    const match = url.match(/[?&]v=([^&]+)/);
    if (match) return match[1];
    // youtu.be/ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) return shortMatch[1];
    // youtube.com/shorts/ID
    const shortsMatch = url.match(/youtube\.com\/shorts\/([^?&/]+)/);
    if (shortsMatch) return shortsMatch[1];
    // youtube.com/embed/ID
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&/]+)/);
    if (embedMatch) return embedMatch[1];
    // youtube.com/live/ID
    const liveMatch = url.match(/youtube\.com\/live\/([^?&/]+)/);
    if (liveMatch) return liveMatch[1];
  } catch (e) {}
  return null;
}

// Converts gallery.php URL to first thumbnail image URL
// e.g. https://smruti.ihariprabodham.in/gallery.php?year=2021&month=08&folder=2021-08-01
// =>   https://smruti.ihariprabodham.in/2021/08/2021-08-01/thumbnail/2021-08-01-01.jpg
function getPhotoThumbnailUrl(photourl, seq = "01") {
  if (!photourl || photourl === "NULL") return null;
  try {
    const url = new URL(photourl);
    const year = url.searchParams.get("year");
    const month = url.searchParams.get("month");
    const folder = url.searchParams.get("folder");
    if (year && month && folder) {
      const datepart = folder.match(/^\d{4}-\d{2}-\d{2}/)?.[0] || folder;
      return `https://smruti.ihariprabodham.in/${year}/${month}/${folder}/thumbnail/${datepart}-${seq}.jpg`;
    }
  } catch (e) {}
  return null;
}

function VicharanDarshan() {
  const [allData, setAllData] = useState([]);
  const [prasangByDate, setPrasangByDate] = useState({});
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { type: 'info'|'paramrut', content }
  const [prasangModal, setPrasangModal] = useState(null);
  const [prasangIndex, setPrasangIndex] = useState(0);

  // Derive available years from data
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [viewMode, setViewMode] = useState("month");
  const [weekStart, setWeekStart] = useState(null);

  // Pradesh / City filters
  const [selectedPradesh, setSelectedPradesh] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [pratishthaOnly, setPratishthaOnly] = useState(false);
  const [shibirOnly, setShibirOnly] = useState(false);
  const [staffSabhaOnly, setStaffSabhaOnly] = useState(false);
  const [utsavOnly, setUtsavOnly] = useState(false);
  const [highlightDateKey, setHighlightDateKey] = useState(null);

  useEffect(() => {
    fetch("/dailydetail/dailydetail-12.json")
      .then((res) => res.json())
      .then((data) => {
        setAllData(data);
        const years = [...new Set(data.map((d) => d.infoyear))].sort();
        setAvailableYears(years);
        if (years.length > 0) {
          const firstYear = years[0];
          setSelectedYear(firstYear);
          const monthsInYear = data
            .filter((d) => d.infoyear === firstYear)
            .map((d) => d.infomonth);
          const firstMonth = Math.min(...monthsInYear);
          setSelectedMonth(firstMonth);
          const fd = new Date(firstYear, firstMonth - 1, 1);
          const fs = new Date(fd);
          fs.setDate(fd.getDate() - fd.getDay());
          setWeekStart(fs);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetch("/data/prasang-index.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load Prasang index");
        return res.json();
      })
      .then((data) => {
        const grouped = data.reduce((dates, prasang) => {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(prasang.engdate || "")) {
            return dates;
          }
          if (!dates[prasang.engdate]) dates[prasang.engdate] = [];
          dates[prasang.engdate].push(prasang);
          return dates;
        }, {});
        setPrasangByDate(grouped);
      })
      .catch((error) => console.error("Error loading Prasang index", error));
  }, []);

  useEffect(() => {
    if (!prasangModal) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [prasangModal]);

  const openPrasangModal = (date, entries) => {
    setPrasangIndex(0);
    setPrasangModal({ date, entries });
  };

  const closePrasangModal = () => {
    setPrasangModal(null);
    setPrasangIndex(0);
  };

  const goToPrevMonth = () => {
    let m = selectedMonth - 1;
    let y = selectedYear;
    if (m < 1) {
      m = 12;
      y = y - 1;
    }
    setSelectedYear(y);
    setSelectedMonth(m);
  };

  const goToNextMonth = () => {
    let m = selectedMonth + 1;
    let y = selectedYear;
    if (m > 12) {
      m = 1;
      y = y + 1;
    }
    setSelectedYear(y);
    setSelectedMonth(m);
  };

  const goToPrevWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  };

  const goToNextWeek = () => {
    setWeekStart((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  // Available pradesh/city options (derived from full dataset)
  const pradeshList = [
    ...new Set(allData.map((d) => (d.pradesh || "").trim()).filter(Boolean)),
  ].sort(gujaratiCollator.compare);
  const cityList = [
    ...new Set(
      allData
        .filter(
          (d) =>
            !selectedPradesh || (d.pradesh || "").trim() === selectedPradesh,
        )
        .map((d) => (d.city || "").trim())
        .filter(Boolean),
    ),
  ].sort(gujaratiCollator.compare);

  const filteredData = allData.filter((d) => {
    if (pratishthaOnly && Number(d.Pratishtha) !== 1) return false;
    if (shibirOnly && Number(d.Shibir) !== 1) return false;
    if (staffSabhaOnly && Number(d.StaffSabha) !== 1) return false;
    if (utsavOnly && Number(d.Utsav) !== 1) return false;
    if (selectedPradesh && (d.pradesh || "").trim() !== selectedPradesh)
      return false;
    if (selectedCity && (d.city || "").trim() !== selectedCity) return false;
    return true;
  });

  const handlePradeshChange = (value) => {
    setSelectedPradesh(value);
    setSelectedCity("");
    setPratishthaOnly(false);
    setShibirOnly(false);
    setStaffSabhaOnly(false);
    setUtsavOnly(false);
    setHighlightDateKey(null);
  };

  const handleCityChange = (value) => {
    setSelectedPradesh("");
    setSelectedCity(value);
    setPratishthaOnly(false);
    setShibirOnly(false);
    setStaffSabhaOnly(false);
    setUtsavOnly(false);
    setHighlightDateKey(null);
  };

  const handlePratishthaToggle = () => {
    setPratishthaOnly((prev) => {
      const next = !prev;
      setShibirOnly(false);
      setStaffSabhaOnly(false);
      setUtsavOnly(false);
      return next;
    });
    setHighlightDateKey(null);
  };

  const handleShibirToggle = () => {
    setShibirOnly((prev) => {
      const next = !prev;
      setPratishthaOnly(false);
      setStaffSabhaOnly(false);
      setUtsavOnly(false);
      return next;
    });
    setHighlightDateKey(null);
  };

  const handleStaffSabhaToggle = () => {
    setStaffSabhaOnly((prev) => {
      const next = !prev;
      setPratishthaOnly(false);
      setShibirOnly(false);
      setUtsavOnly(false);
      return next;
    });
    setHighlightDateKey(null);
  };

  const handleUtsavToggle = () => {
    setUtsavOnly((prev) => {
      const next = !prev;
      setPratishthaOnly(false);
      setShibirOnly(false);
      setStaffSabhaOnly(false);
      return next;
    });
    setHighlightDateKey(null);
  };

  const clearFilters = () => {
    setSelectedPradesh("");
    setSelectedCity("");
    setPratishthaOnly(false);
    setShibirOnly(false);
    setStaffSabhaOnly(false);
    setUtsavOnly(false);
    setHighlightDateKey(null);
  };

  // Jump the calendar to the date of a filtered entry
  const goToEntry = (entry) => {
    setSelectedYear(entry.infoyear);
    setSelectedMonth(entry.infomonth);
    setHighlightDateKey(
      getDateKey(entry.infoyear, entry.infomonth, entry.infoday),
    );
    if (viewMode === "week") {
      const d = new Date(entry.infoyear, entry.infomonth - 1, entry.infoday);
      const fs = new Date(d);
      fs.setDate(d.getDate() - d.getDay());
      setWeekStart(fs);
    }
  };

  // Build map: day -> entry for selected month/year
  const dayMap = {};
  filteredData
    .filter((d) => d.infoyear === selectedYear && d.infomonth === selectedMonth)
    .forEach((d) => {
      if (!dayMap[d.infoday]) dayMap[d.infoday] = [];
      dayMap[d.infoday].push(d);
    });

  // Build calendar grid
  const renderCalendar = () => {
    if (!selectedYear || !selectedMonth) return null;
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const cells = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="vd-cell vd-cell--empty" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const entries = dayMap[day] || [];
      const displayEntries = entries.slice(0, 2);
      const dateKey = getDateKey(selectedYear, selectedMonth, day);
      const prasangs = prasangByDate[dateKey] || [];
      cells.push(
        <div
          key={day}
          className={`vd-cell ${displayEntries.length > 0 || prasangs.length > 0 ? "vd-cell--active" : ""} ${displayEntries.length > 1 ? "vd-cell--two" : ""} ${dateKey === highlightDateKey ? "vd-cell--highlight" : ""}`}
        >
          <span className="vd-day-num">{day}</span>
          {displayEntries.map((entry, idx) => (
            <div
              key={idx}
              className={`vd-cell-body ${displayEntries.length > 1 ? "vd-cell-body--split" : ""}`}
            >
              {displayEntries.length > 1 && (
                <div className="vd-entry-divider" />
              )}
              <div className="vd-city">{entry.place}</div>
              {entry.shortdescguj && (
                <div
                  className="vd-desc-preview"
                  onClick={() =>
                    setModal({
                      type: "info",
                      content: entry.shortdescguj,
                      village: entry.village,
                    })
                  }
                >
                  {entry.shortdescguj}
                </div>
              )}
              <div className="vd-thumbnails">
                {getYoutubeId(entry.videourl) && (
                  <a
                    href={entry.videourl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch on YouTube"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.videourl)}/mqdefault.jpg`}
                      alt="YouTube"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--yt">▶</span>
                  </a>
                )}
                {entry.photourl && entry.photourl !== "NULL" && (
                  <a
                    href={entry.photourl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="View Photos"
                  >
                    <img
                      src={
                        getPhotoThumbnailUrl(entry.photourl) ||
                        "/dailydetail/thumbnail.jpg"
                      }
                      alt="Photos"
                      className="vd-thumb vd-thumb--photo"
                      onError={(e) => {
                        const alt001 = getPhotoThumbnailUrl(
                          entry.photourl,
                          "001",
                        );
                        if (alt001 && e.target.src !== alt001) {
                          e.target.src = alt001;
                        } else {
                          e.target.src = "/dailydetail/thumbnail.jpg";
                        }
                      }}
                    />
                  </a>
                )}
                {getYoutubeId(entry.highlighturl) && (
                  <a
                    href={entry.highlighturl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch Highlight"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.highlighturl)}/mqdefault.jpg`}
                      alt="Highlight"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--hl">H</span>
                  </a>
                )}
                {getYoutubeId(entry.clipurl1) && (
                  <a
                    href={entry.clipurl1}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch Clip 1"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.clipurl1)}/mqdefault.jpg`}
                      alt="Clip 1"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--cl">
                      C1
                    </span>
                  </a>
                )}
                {getYoutubeId(entry.clipurl2) && (
                  <a
                    href={entry.clipurl2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch Clip 2"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.clipurl2)}/mqdefault.jpg`}
                      alt="Clip 2"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--cl">
                      C2
                    </span>
                  </a>
                )}
              </div>
              <div className="vd-icons">
                {entry.flagparamrutapp === 1 && (
                  <button
                    className="vd-icon-btn vd-icon-btn--p"
                    title="Pravachan on Paramrut App"
                    onClick={() =>
                      setModal({ type: "paramrut", content: null })
                    }
                  >
                    <img
                      src="/image/paramrut.jpg"
                      alt="Paramrut"
                      className="vd-paramrut-icon"
                    />
                  </button>
                )}
                {entry.flagpatrikaapp === 1 && (
                  <button
                    className="vd-icon-btn vd-icon-btn--h"
                    title="Vicharan Darshan on Hariprabodham App"
                    onClick={() =>
                      setModal({ type: "hariprabodham", content: null })
                    }
                  >
                    <img
                      src="/image/hariprabodham.png"
                      alt="Hariprabodham"
                      className="vd-hariprabodham-icon"
                    />
                  </button>
                )}
                {entry.flagpatrika === 1 && (
                  <button
                    className="vd-icon-btn vd-icon-btn--h"
                    title="Akshar Dhara Patrika"
                    onClick={() => setModal({ type: "patrika", content: null })}
                  >
                    <img
                      src="/image/hariprabodham.png"
                      alt="Akshar Dhara Patrika"
                      className="vd-hariprabodham-icon"
                    />
                  </button>
                )}

                {!!entry.latitude && !!entry["longitude"] && (
                  <a
                    href={`https://www.google.com/maps?q=${entry.latitude},${entry["longitude"]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-icon-btn vd-icon-btn--map"
                    title="Open Map"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="vd-map-svg"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          ))}
          {prasangs.length > 0 && (
            <button
              className="vd-prasang-link"
              onClick={() => openPrasangModal(dateKey, prasangs)}
            >
              Prasang ({prasangs.length})
            </button>
          )}
        </div>,
      );
    }
    return cells;
  };

  const renderWeekCalendar = () => {
    if (!weekStart) return null;
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(weekStart.getTime() + i * 86400000);
      const y = date.getFullYear();
      const m = date.getMonth() + 1;
      const d = date.getDate();
      const entries = filteredData.filter(
        (e) => e.infoyear === y && e.infomonth === m && e.infoday === d,
      );
      const displayEntries = entries.slice(0, 2);
      const dateKey = getDateKey(y, m, d);
      const prasangs = prasangByDate[dateKey] || [];
      return (
        <div
          key={i}
          className={`vd-cell ${displayEntries.length > 0 || prasangs.length > 0 ? "vd-cell--active" : ""} ${displayEntries.length > 1 ? "vd-cell--two" : ""} ${dateKey === highlightDateKey ? "vd-cell--highlight" : ""}`}
        >
          <span className="vd-day-num">
            {monthNames[m - 1].slice(0, 3)} {d}
          </span>
          {displayEntries.map((entry, idx) => (
            <div
              key={idx}
              className={`vd-cell-body ${displayEntries.length > 1 ? "vd-cell-body--split" : ""}`}
            >
              {displayEntries.length > 1 && (
                <div className="vd-entry-divider" />
              )}
              <div className="vd-city">{entry.place}</div>
              {entry.shortdescguj && (
                <div
                  className="vd-desc-preview"
                  onClick={() =>
                    setModal({
                      type: "info",
                      content: entry.shortdescguj,
                      village: entry.village,
                    })
                  }
                >
                  {entry.shortdescguj}
                </div>
              )}
              <div className="vd-thumbnails">
                {getYoutubeId(entry.videourl) && (
                  <a
                    href={entry.videourl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch on YouTube"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.videourl)}/mqdefault.jpg`}
                      alt="YouTube"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--yt">▶</span>
                  </a>
                )}
                {entry.photourl && entry.photourl !== "NULL" && (
                  <a
                    href={entry.photourl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="View Photos"
                  >
                    <img
                      src={
                        getPhotoThumbnailUrl(entry.photourl) ||
                        "/dailydetail/thumbnail.jpg"
                      }
                      alt="Photos"
                      className="vd-thumb vd-thumb--photo"
                      onError={(e) => {
                        const alt001 = getPhotoThumbnailUrl(
                          entry.photourl,
                          "001",
                        );
                        if (alt001 && e.target.src !== alt001) {
                          e.target.src = alt001;
                        } else {
                          e.target.src = "/dailydetail/thumbnail.jpg";
                        }
                      }}
                    />
                  </a>
                )}
                {getYoutubeId(entry.highlighturl) && (
                  <a
                    href={entry.highlighturl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch Highlight"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.highlighturl)}/mqdefault.jpg`}
                      alt="Highlight"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--hl">H</span>
                  </a>
                )}
                {getYoutubeId(entry.clipurl1) && (
                  <a
                    href={entry.clipurl1}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch Clip 1"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.clipurl1)}/mqdefault.jpg`}
                      alt="Clip 1"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--cl">
                      C1
                    </span>
                  </a>
                )}
                {getYoutubeId(entry.clipurl2) && (
                  <a
                    href={entry.clipurl2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-thumb-link"
                    title="Watch Clip 2"
                  >
                    <img
                      src={`https://img.youtube.com/vi/${getYoutubeId(entry.clipurl2)}/mqdefault.jpg`}
                      alt="Clip 2"
                      className="vd-thumb vd-thumb--yt"
                    />
                    <span className="vd-thumb-badge vd-thumb-badge--cl">
                      C2
                    </span>
                  </a>
                )}
              </div>
              <div className="vd-icons">
                {entry.flagparamrutapp === 1 && (
                  <button
                    className="vd-icon-btn vd-icon-btn--p"
                    title="Pravachan on Paramrut App"
                    onClick={() =>
                      setModal({ type: "paramrut", content: null })
                    }
                  >
                    <img
                      src="/image/paramrut.jpg"
                      alt="Paramrut"
                      className="vd-paramrut-icon"
                    />
                  </button>
                )}
                {entry.flagpatrikaapp === 1 && (
                  <button
                    className="vd-icon-btn vd-icon-btn--h"
                    title="Vicharan Darshan on Hariprabodham App"
                    onClick={() =>
                      setModal({ type: "hariprabodham", content: null })
                    }
                  >
                    <img
                      src="/image/hariprabodham.png"
                      alt="Hariprabodham"
                      className="vd-hariprabodham-icon"
                    />
                  </button>
                )}
                {entry.flagpatrika === 1 && (
                  <button
                    className="vd-icon-btn vd-icon-btn--h"
                    title="Akshar Dhara Patrika"
                    onClick={() => setModal({ type: "patrika", content: null })}
                  >
                    <img
                      src="/image/hariprabodham.png"
                      alt="Akshar Dhara Patrika"
                      className="vd-hariprabodham-icon"
                    />
                  </button>
                )}
                {!!entry.latitude && !!entry["longitude"] && (
                  <a
                    href={`https://www.google.com/maps?q=${entry.latitude},${entry["longitude"]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vd-icon-btn vd-icon-btn--map"
                    title="Open Map"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="vd-map-svg"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          ))}
          {prasangs.length > 0 && (
            <button
              className="vd-prasang-link"
              onClick={() => openPrasangModal(dateKey, prasangs)}
            >
              Prasang ({prasangs.length})
            </button>
          )}
        </div>
      );
    });
  };

  const closeModal = () => setModal(null);

  return (
    <div className="vd-page">
      <div className="vd-page-header">
        <h1 className="vd-main-title">Guruhari Vicharan Darshan</h1>
        <p className="vd-sub-title">ગુરુહરિ વિચરણ દર્શન</p>
      </div>

      {loading ? (
        <div className="vd-loading">Loading...</div>
      ) : (
        <div className="vd-layout">
          {/* Filter panel */}
          <aside className="vd-filter-panel">
            <h3 className="vd-filter-title">Filter</h3>

            <button
              type="button"
              className={`vd-filter-pratishtha-btn${pratishthaOnly ? " vd-filter-pratishtha-btn--active" : ""}`}
              aria-pressed={pratishthaOnly}
              onClick={handlePratishthaToggle}
            >
              Murti Pratishtha
            </button>

            <button
              type="button"
              className={`vd-filter-pratishtha-btn${shibirOnly ? " vd-filter-pratishtha-btn--active" : ""}`}
              aria-pressed={shibirOnly}
              onClick={handleShibirToggle}
            >
              Shibir
            </button>

            <button
              type="button"
              className={`vd-filter-pratishtha-btn${utsavOnly ? " vd-filter-pratishtha-btn--active" : ""}`}
              aria-pressed={utsavOnly}
              onClick={handleUtsavToggle}
            >
              Utsav
            </button>

            <button
              type="button"
              className={`vd-filter-pratishtha-btn${staffSabhaOnly ? " vd-filter-pratishtha-btn--active" : ""}`}
              aria-pressed={staffSabhaOnly}
              onClick={handleStaffSabhaToggle}
            >
              Staff Sabha
            </button>
            <label className="vd-filter-label" htmlFor="vd-pradesh-filter">
              Pradesh
            </label>
            <select
              id="vd-pradesh-filter"
              className="vd-filter-select"
              value={selectedPradesh}
              onChange={(e) => handlePradeshChange(e.target.value)}
            >
              <option value="">All Pradesh</option>
              {pradeshList.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>

            <label className="vd-filter-label" htmlFor="vd-city-filter">
              City
            </label>
            <select
              id="vd-city-filter"
              className="vd-filter-select"
              value={selectedCity}
              onChange={(e) => handleCityChange(e.target.value)}
            >
              <option value="">All Cities</option>
              {cityList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            <button
              type="button"
              className="vd-filter-clear"
              onClick={clearFilters}
            >
              Clear Filters
            </button>

            <div className="vd-filter-count">
              {filteredData.length} entr
              {filteredData.length !== 1 ? "ies" : "y"}
            </div>

            {(selectedPradesh ||
              selectedCity ||
              pratishthaOnly ||
              shibirOnly ||
              staffSabhaOnly ||
              utsavOnly) && (
              <div className="vd-filter-results">
                <h4 className="vd-filter-results-title">Matching Entries</h4>
                <ul className="vd-filter-results-list">
                  {filteredData.map((entry) => {
                    const dateKey = getDateKey(
                      entry.infoyear,
                      entry.infomonth,
                      entry.infoday,
                    );
                    return (
                      <li
                        key={entry.infoid}
                        className={`vd-filter-result-item${dateKey === highlightDateKey ? " vd-filter-result-item--active" : ""}`}
                        aria-current={dateKey === highlightDateKey ? "true" : undefined}
                        onClick={() => goToEntry(entry)}
                      >
                        <span className="vd-filter-result-date">
                          {entry.infodate}
                        </span>
                        <span className="vd-filter-result-place">
                          {entry.place}
                        </span>
                      </li>
                    );
                  })}
                  {filteredData.length === 0 && (
                    <li className="vd-filter-result-empty">
                      No matching entries.
                    </li>
                  )}
                </ul>
              </div>
            )}
          </aside>

          <div className="vd-content">
            {/* Year tabs */}
            <div className="vd-year-tabs" role="group" aria-label="Years">
              {availableYears.map((y) => (
                <button
                  key={y}
                  className={`vd-year-tab ${selectedYear === y ? "vd-year-tab--active" : ""}`}
                  aria-pressed={selectedYear === y}
                  onClick={() => {
                    setSelectedYear(y);
                    const monthsInYear = allData
                      .filter((d) => d.infoyear === y)
                      .map((d) => d.infomonth);
                    const firstM = Math.min(...monthsInYear);
                    setSelectedMonth(firstM);
                    const fd = new Date(y, firstM - 1, 1);
                    const fs = new Date(fd);
                    fs.setDate(fd.getDate() - fd.getDay());
                    setWeekStart(fs);
                  }}
                >
                  {y}
                </button>
              ))}
            </div>

            {/* View mode toggle */}
            <div className="vd-view-toggle" role="group" aria-label="Calendar view">
              <button
                className={`vd-toggle-btn ${viewMode === "month" ? "vd-toggle-btn--active" : ""}`}
                aria-pressed={viewMode === "month"}
                onClick={() => setViewMode("month")}
              >
                Month
              </button>
              <button
                className={`vd-toggle-btn ${viewMode === "week" ? "vd-toggle-btn--active" : ""}`}
                aria-pressed={viewMode === "week"}
                onClick={() => setViewMode("week")}
              >
                Week
              </button>
            </div>

            {/* Month navigation */}
            {viewMode === "month" && (
              <div className="vd-month-nav">
                <button className="vd-nav-btn" onClick={goToPrevMonth}>
                  &#8249; Prev
                </button>
                <h2 className="vd-month-title">
                  {selectedYear} – {monthNames[selectedMonth - 1]}
                </h2>
                <button className="vd-nav-btn" onClick={goToNextMonth}>
                  Next &#8250;
                </button>
              </div>
            )}

            {/* Week navigation */}
            {viewMode === "week" && weekStart && (
              <div className="vd-month-nav">
                <button className="vd-nav-btn" onClick={goToPrevWeek}>
                  &#8249; Prev
                </button>
                <h2 className="vd-month-title">
                  {(() => {
                    const we = new Date(weekStart.getTime() + 6 * 86400000);
                    return `${monthNames[weekStart.getMonth()]} ${weekStart.getDate()} – ${monthNames[we.getMonth()]} ${we.getDate()}, ${we.getFullYear()}`;
                  })()}
                </h2>
                <button className="vd-nav-btn" onClick={goToNextWeek}>
                  Next &#8250;
                </button>
              </div>
            )}

            {/* Calendar grid */}
            <div
              className={`vd-calendar${viewMode === "week" ? " vd-calendar--week" : ""}`}
            >
              {dayNames.map((d) => (
                <div key={d} className="vd-day-header">
                  {d}
                </div>
              ))}
              {viewMode === "month" ? renderCalendar() : renderWeekCalendar()}
            </div>

            {/* Monthly summary */}
            <div className="vd-summary">
              <span className="vd-summary-item">
                <span className="vd-summary-label">Sabha</span>
                <span className="vd-summary-value">
                  {
                    filteredData.filter(
                      (d) =>
                        d.infoyear === selectedYear &&
                        d.infomonth === selectedMonth &&
                        d.flagsabha === 1,
                    ).length
                  }
                </span>
              </span>
              <span className="vd-summary-item">
                <span className="vd-summary-label">Paramrut App</span>
                <span className="vd-summary-value">
                  {
                    filteredData.filter(
                      (d) =>
                        d.infoyear === selectedYear &&
                        d.infomonth === selectedMonth &&
                        d.flagparamrutapp === 1,
                    ).length
                  }
                </span>
              </span>
              <span className="vd-summary-item">
                <span className="vd-summary-label">Vicharan Report</span>
                <span className="vd-summary-value">
                  {
                    filteredData.filter(
                      (d) =>
                        d.infoyear === selectedYear &&
                        d.infomonth === selectedMonth &&
                        d.flagpatrikaapp === 1,
                    ).length
                  }
                </span>
              </span>
              <span className="vd-summary-item">
                <span className="vd-summary-label">Akshar Dhara Patrika</span>
                <span className="vd-summary-value">
                  {
                    filteredData.filter(
                      (d) =>
                        d.infoyear === selectedYear &&
                        d.infomonth === selectedMonth &&
                        d.flagpatrika === 1,
                    ).length
                  }
                </span>
              </span>
              <span className="vd-summary-item">
                <span className="vd-summary-label">Videos</span>
                <span className="vd-summary-value">
                  {
                    filteredData.filter(
                      (d) =>
                        d.infoyear === selectedYear &&
                        d.infomonth === selectedMonth &&
                        d.videourl &&
                        d.videourl !== "NULL" &&
                        d.videourl.trim() !== "",
                    ).length
                  }
                </span>
              </span>
              <span className="vd-summary-item">
                <span className="vd-summary-label">Photos</span>
                <span className="vd-summary-value">
                  {
                    filteredData.filter(
                      (d) =>
                        d.infoyear === selectedYear &&
                        d.infomonth === selectedMonth &&
                        d.photourl &&
                        d.photourl !== "NULL" &&
                        d.photourl.trim() !== "",
                    ).length
                  }
                </span>
              </span>
              <span className="vd-summary-item">
                <span className="vd-summary-label">Prasang</span>
                <span className="vd-summary-value">
                  {Object.entries(prasangByDate)
                    .filter(([date]) =>
                      date.startsWith(
                        `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-`,
                      ),
                    )
                    .reduce((count, [, entries]) => count + entries.length, 0)}
                </span>
              </span>
              <span className="vd-summary-item">
                <span className="vd-summary-label">City</span>
                <span className="vd-summary-value">
                  {
                    new Set(
                      filteredData
                        .filter(
                          (d) =>
                            d.infoyear === selectedYear &&
                            d.infomonth === selectedMonth,
                        )
                        .map((d) => (d.city || "").trim())
                        .filter(Boolean),
                    ).size
                  }
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="vd-modal-overlay" onClick={closeModal}>
          <div className="vd-modal" onClick={(e) => e.stopPropagation()}>
            <button className="vd-modal-close" aria-label="Close details" onClick={closeModal}>
              ✕
            </button>
            {modal.type === "paramrut" && (
              <>
                <img
                  src="/image/paramrut.jpg"
                  alt="Paramrut"
                  className="vd-modal-paramrut-img"
                />
                <p className="vd-modal-text">
                  Pravachan Is Available In Paramrut App
                </p>
              </>
            )}
            {modal.type === "hariprabodham" && (
              <>
                <img
                  src="/image/hariprabodham.png"
                  alt="Hariprabodham"
                  className="vd-modal-hariprabodham-img"
                />
                <p className="vd-modal-text">
                  Vicharan Darshan Report Is Available in Hariprabodham App
                </p>
              </>
            )}
            {modal.type === "patrika" && (
              <>
                <img
                  src="/image/hariprabodham.png"
                  alt="Akshar Dhara Patrika"
                  className="vd-modal-hariprabodham-img"
                />
                <p className="vd-modal-text">
                  The Details Of Utsav/Sabha/Vicharan Is Available in 'Akshar
                  Dhara' Patrika.
                </p>
              </>
            )}
            {modal.type === "info" && (
              <>
                <div className="vd-modal-icon">વિગત</div>
                <p className="vd-modal-text vd-modal-text--guj">
                  {modal.content}
                </p>
                {modal.village && (
                  <p className="vd-modal-village">{modal.village}</p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {prasangModal && (
        <div className="vd-prasang-modal-overlay" onClick={closePrasangModal}>
          <div
            className="vd-prasang-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="vd-prasang-modal-header">
              <h2>Prasang - {prasangModal.date}</h2>
              <button
                className="vd-prasang-modal-close"
                aria-label="Close Prasang"
                onClick={closePrasangModal}
              >
                ×
              </button>
            </div>
            {prasangModal.entries.length > 1 && (
              <div className="vd-prasang-navigation">
                <button
                  aria-label="Previous Prasang"
                  disabled={prasangIndex === 0}
                  onClick={() =>
                    setPrasangIndex((index) => Math.max(index - 1, 0))
                  }
                >
                  &#8249;
                </button>
                <span>
                  {prasangIndex + 1} / {prasangModal.entries.length}
                </span>
                <button
                  aria-label="Next Prasang"
                  disabled={prasangIndex === prasangModal.entries.length - 1}
                  onClick={() =>
                    setPrasangIndex((index) =>
                      Math.min(index + 1, prasangModal.entries.length - 1),
                    )
                  }
                >
                  &#8250;
                </button>
              </div>
            )}
            <div className="vd-prasang-reader">
              <h3>{prasangModal.entries[prasangIndex].title}</h3>
              <ThemedReaderFrame
                documentClassName="calendar-embed"
                src={`/data/prasang/${prasangModal.entries[prasangIndex].file}`}
                title={prasangModal.entries[prasangIndex].title}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VicharanDarshan;
